// ==============================================================================================
//  APPLICATION AUTHORSHIP & RIGHTS NOTICE
//
//  Developed by: Gareth Morgan
//  Brand & Product: BuildMyChecks.co.uk
//  Ownership: Sole author and rights holder
//
//  ----------------------------------------------------------------------------------------------
//  NOTICE:
//  - This application and its related systems were designed, developed, and built solely
//    by Gareth Morgan.
//  - Gareth Morgan retains full authorship, intellectual property, and creative rights to
//    all Build My Checks branding, technology, source code, architecture, and design.
//  - No ownership, license, or implied rights are granted to any third party unless
//    explicitly agreed in writing by Gareth Morgan.
//  - Gareth Morgan is NOT responsible for future maintenance, updates, security patching,
//    or operational upkeep if this application is deployed, copied, or modified without
//    his written consent.
//  - Gareth Morgan reserves the right to:
//        Withdraw or disable access to the application or related services
//        Remove hosted, cloud-based, or linked services under his control
//        Revoke access to Build My Checksrelated assets, code, APIs, or integrations
//
//   2025 Gareth Morgan  BuildMyChecks.co.uk
//  Unauthorized use, alteration, copying, or redistribution without written consent
//  is strictly prohibited.
//
//  ----------------------------------------------------------------------------------------------
//  Build My Checks  ASCII Logo Signature
//  (Symbolic representation of BuildMyChecks.co.uk identity)
//
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................:-==++-.........:-:.............................................
// ...................................-*******:.......=*****+-.........................................
// ...................................=*******+:.....+*******=.........................................
// .........................::........+**********************=.........................................
// .......................-+***+-.:-++***********************=:.......:................................
// .....................-+*************************************+-..=+**+-..............................
// .....................:+*************+=-.............-++***************+:............................
// ......................:+********+=:.....................:=*************=............................
// .......................=******+:...........................-+********+-.............................
// ......................-*****+:...............................-+*****+-..............................
// ................:::::+****+-...................................=*****+..............................
// ...............+-......:=+-........+=.*%=......##:.:::.....:::..-*****=.............................
// ..............=*-*@%%%%@*.........-#*:#@=......@%::@@@*...*@@#...-+++++**++=-.......................
// .............:**-*@*..=@%.=#+..*#=-%#:#@=.=#%#+@%::@%%%-.=@%@#-##:..##:+*****:......................
// ..............-+-*@@%%@%+.+@*..#@==%#:#@==@%-:+@%::@#-@#:%#+%#.+@#.*@+-******=......................
// .................*@*..:#@++@*..#@==%#:#@=+@#...@%::@#.+@%%-=%#..%@#%#:+******+......................
// .................*@%##%@#.-%@%%@@==%#:#@=:#@%#%%%::@#.:#@*.=%#...%@%:=****+-:.......................
// ..................::........::.........:....::.....:....:......:-#@--***+:..........................
// ................:++:+@@@@@*.:@#:.....................#%=......:#%#-.+***+:..........................
// .............-+**+:#@*:.:+%+:@#-=+-...:=+=-....-+=-..#%=.:=-..-++-..+***+:..........................
// .............+***=-@%.......:@@#+#@#.+@#-+%#:+@%++@%:#%+*@#::%#--*-:*****+-.........................
// .............-***=:@%.......:@%:.-%%:%@%%%%%+#@=.....#@@@%:..#%@@%-=**+++++*+.......................
// ..............****-+%@*=+%@*:@%:.-%%.*@*-=*=.*@%==%#:#@+=@%=:++-+@*-+--===:+=.......................
// .................::::=***=:..++..:++..-+**=...:+**+:.=+-.:++:-*#*=::-:+***=::-++:...................
// ....................-****+=............................*#......+*****************=..................
// .....................=*****=.......+##+.:+##=....*+.-#-*#.**:..:+************++*=...................
// .....................+******+-....*%..:.%*.:%+..:%*.=%=*%%*..:--***********+:..-+:..................
// ....................+*********=:%=+%*+%-*%+*%-=#-#%*%@=*#:%*:+****+++****+=...=*****-...............
// ...................=**********+--::.::....::...:..::.:.:-::-:=***+:..=+*+:..:+******-...............
// ....................-******++*******+=-:............-=++****+=+***+:...:...=******+-:...............
// ......................-++-....=+***************************+=+-=****+:...-+******-..................
// ................................=**********************++-:....=******=:+********=..................
// ................................=**********************=......:+*****************+:.................
// ................................+*******=......+*******=........-+=.:=+****+-.-+-...................
// ................................:-=+***-........+******=..............=***+.........................
// .................................................--::...............................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
//
// ==============================================================================================

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, onSnapshot, setDoc, updateDoc, deleteDoc, arrayRemove } from 'firebase/firestore';
import { auth, db } from '../../firebase';

export default function UserManagement() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDevices, setUserDevices] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        const companyId = userData?.companyId;

        if (!companyId) {
          navigate('/setup');
          return;
        }

        // Load company data
        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        setCompany({ id: companyId, ...companyDoc.data() });

        // Subscribe to users in real-time
        const usersQuery = query(collection(db, 'companies', companyId, 'users'));
        const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
          const usersList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          setUsers(usersList);
        });

        setLoading(false);
        return () => unsubscribe();
      } catch (err) {
        console.error('Failed to load user management:', err);
        setLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  const handleInviteUser = async () => {
    setError('');
    
    if (!inviteEmail) {
      setError('Email is required');
      return;
    }

    // Check if we've reached user limit
    if (users.length >= (company?.userLimit || 0)) {
      setError(`User limit reached (${company?.userLimit} users). Increase your subscription to add more users.`);
      return;
    }

    setInviting(true);

    try {
      // In production, this would send an invite email via Firebase Functions/Cloud Functions
      // For now, we'll create a placeholder user entry
      const userId = `pending_${Date.now()}`;
      
      await setDoc(doc(db, 'companies', company.id, 'users', userId), {
        email: inviteEmail,
        role: inviteRole,
        status: 'pending',
        invitedAt: new Date().toISOString(),
        invitedBy: auth.currentUser.uid,
      });

      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('user');
      alert(`Invitation sent to ${inviteEmail}!\n\nNote: In production, they would receive an email to complete registration.`);
    } catch (err) {
      console.error('Failed to invite user:', err);
      setError(err.message || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'companies', company.id, 'users', userId), {
        role: newRole,
      });
      alert(`User role updated to ${newRole}`);
    } catch (err) {
      console.error('Failed to update role:', err);
      alert('Failed to update user role');
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (!window.confirm(`Remove ${userEmail} from your company?`)) return;

    try {
      await deleteDoc(doc(db, 'companies', company.id, 'users', userId));
      alert('User removed successfully');
    } catch (err) {
      console.error('Failed to remove user:', err);
      alert('Failed to remove user');
    }
  };

  const handleManageDevices = async (user) => {
    try {
      // Get user's device data from the main users collection
      const userDoc = await getDoc(doc(db, 'users', user.id));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserDevices(userData.devices || []);
        setSelectedUser(user);
        setShowDeviceModal(true);
      } else {
        alert('User data not found');
      }
    } catch (err) {
      console.error('Failed to load user devices:', err);
      alert('Failed to load user devices: ' + err.message);
    }
  };

  const handleRemoveDevice = async (deviceId) => {
    if (!selectedUser) return;
    
    try {
      const deviceToRemove = userDevices.find(d => d.id === deviceId);
      await updateDoc(doc(db, 'users', selectedUser.id), {
        devices: arrayRemove(deviceToRemove)
      });
      
      // Update local state
      setUserDevices(userDevices.filter(d => d.id !== deviceId));
      alert('Device removed successfully');
    } catch (err) {
      console.error('Failed to remove device:', err);
      alert('Failed to remove device: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-50 relative overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -left-24 -top-32 w-80 h-80 bg-red-600/20 blur-3xl rounded-full" />
          <div className="absolute right-16 top-10 w-96 h-96 bg-rose-500/18 blur-3xl rounded-full" />
          <div className="absolute -right-32 bottom-16 w-72 h-72 bg-amber-400/12 blur-3xl rounded-full" />
        </div>
        <div className="relative z-10 flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-2 border-rose-400/60 border-t-transparent rounded-full animate-spin" />
          <div className="text-gray-200 text-lg">Loading user management...</div>
        </div>
      </div>
    );
  }

  const activeUsers = users.filter(u => u.status !== 'pending');
  const pendingUsers = users.filter(u => u.status === 'pending');
  const canAddMore = users.length < (company?.userLimit || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-24 -top-32 w-80 h-80 bg-red-600/20 blur-3xl rounded-full" />
        <div className="absolute right-16 top-10 w-96 h-96 bg-rose-500/18 blur-3xl rounded-full" />
        <div className="absolute -right-32 bottom-16 w-72 h-72 bg-amber-400/12 blur-3xl rounded-full" />
      </div>

      <header className="relative z-10 border-b border-white/10 bg-white/5 backdrop-blur-xl shadow-lg">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400">Admin</p>
            <h1 className="text-3xl font-bold text-white">User Management</h1>
            <p className="text-sm text-gray-300">{company?.name || 'Company'}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition"
            >
              ← Back to dashboard
            </button>
            <button
              onClick={() => navigate('/subscription')}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-600 to-amber-500 hover:from-rose-500 hover:to-amber-400 text-white shadow-lg shadow-rose-900/30 transition"
            >
              💳 Subscription
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div className="grid md:grid-cols-4 gap-4">
          {[{ label: 'Active users', value: activeUsers.length, accent: 'text-emerald-200', border: 'border-emerald-200/30' },
            { label: 'Pending invites', value: pendingUsers.length, accent: 'text-amber-200', border: 'border-amber-200/30' },
            { label: 'User limit', value: company?.userLimit || 0, accent: 'text-gray-100', border: 'border-white/10' },
            { label: 'Available slots', value: Math.max(0, (company?.userLimit || 0) - users.length), accent: 'text-sky-200', border: 'border-sky-200/30' }].map((item, idx) => (
            <div key={idx} className={`bg-white/5 border ${item.border} rounded-2xl p-4 backdrop-blur-lg shadow-xl`}> 
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{item.label}</p>
              <p className={`text-3xl font-bold ${item.accent}`}>{item.value}</p>
            </div>
          ))}
        </div>

        {!canAddMore && (
          <div className="bg-amber-500/10 border border-amber-200/40 text-amber-100 rounded-2xl p-4 backdrop-blur-lg shadow-lg">
            <p className="text-sm">
              <strong>User limit reached.</strong> Increase your subscription to add more users.
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowInviteModal(true)}
            disabled={!canAddMore}
            className="px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-rose-500 hover:from-emerald-400 hover:to-rose-400 text-white font-semibold shadow-lg shadow-rose-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Invite user
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition"
          >
            ⟳ Refresh
          </button>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl backdrop-blur-lg shadow-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Active users ({activeUsers.length})</h2>
            <span className="text-xs text-gray-400">Real-time sync</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-100">
              <thead className="bg-white/5 border-b border-white/10 text-xs uppercase tracking-wide text-gray-300">
                <tr>
                  <th className="px-6 py-3 text-left">Email</th>
                  <th className="px-6 py-3 text-left">Role</th>
                  <th className="px-6 py-3 text-left">Added</th>
                  <th className="px-6 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {activeUsers.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-6 text-center text-gray-300">No active users yet.</td>
                  </tr>
                ) : (
                  activeUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-white/5">
                      <td className="px-6 py-4 text-sm">{user.email}</td>
                      <td className="px-6 py-4">
                        <select
                          value={user.role}
                          onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border bg-white/5 text-gray-100 ${
                            user.role === 'admin'
                              ? 'border-rose-300/40'
                              : 'border-white/20'
                          }`}
                        >
                          <option value="admin">👑 Admin</option>
                          <option value="user">👤 User</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {user.addedAt ? new Date(user.addedAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3 text-xs">
                          <button
                            onClick={() => handleManageDevices(user)}
                            className="text-sky-200 hover:text-white"
                          >
                            Manage devices
                          </button>
                          {user.id !== auth.currentUser?.uid && (
                            <>
                              <span className="text-gray-600">|</span>
                              <button
                                onClick={() => handleRemoveUser(user.id, user.email)}
                                className="text-rose-200 hover:text-white"
                              >
                                Remove
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {pendingUsers.length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl backdrop-blur-lg shadow-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Pending invites ({pendingUsers.length})</h2>
              <span className="text-xs text-gray-400">Awaiting acceptance</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-gray-100">
                <thead className="bg-white/5 border-b border-white/10 text-xs uppercase tracking-wide text-gray-300">
                  <tr>
                    <th className="px-6 py-3 text-left">Email</th>
                    <th className="px-6 py-3 text-left">Role</th>
                    <th className="px-6 py-3 text-left">Invited</th>
                    <th className="px-6 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {pendingUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-white/5">
                      <td className="px-6 py-4 text-sm">{user.email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                          user.role === 'admin'
                            ? 'bg-rose-500/15 text-rose-100 border-rose-300/40'
                            : 'bg-white/5 text-gray-100 border-white/15'
                        }`}>
                          {user.role === 'admin' ? '👑 Admin' : '👤 User'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {user.invitedAt ? new Date(user.invitedAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleRemoveUser(user.id, user.email)}
                          className="text-rose-200 hover:text-white text-xs"
                        >
                          Cancel invite
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 text-gray-50 border border-white/10 rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-white mb-2">Invite user</h2>
            <p className="text-sm text-gray-300 mb-4">Send an invite email and set their role.</p>
            
            {error && (
              <div className="bg-rose-500/15 border border-rose-300/40 text-rose-100 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Email address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-4 py-2 border border-white/10 rounded-lg bg-white/5 text-gray-50 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-4 py-2 border border-white/10 rounded-lg bg-white/5 text-gray-50 focus:outline-none focus:ring-2 focus:ring-rose-500"
                >
                  <option value="user">User (conduct checks only)</option>
                  <option value="admin">Admin (full access)</option>
                </select>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-gray-100">
                <p><strong>Admin:</strong> Create checklists, manage users, conduct checks.</p>
                <p><strong>User:</strong> Conduct checks and view reports.</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleInviteUser}
                disabled={inviting}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-rose-500 hover:from-emerald-400 hover:to-rose-400 text-white py-3 rounded-lg font-semibold shadow-lg shadow-rose-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inviting ? 'Inviting...' : 'Send invite'}
              </button>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteRole('user');
                  setError('');
                }}
                className="flex-1 bg-white/10 border border-white/15 hover:bg-white/15 text-gray-100 py-3 rounded-lg font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device Management Modal */}
      {showDeviceModal && selectedUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 text-gray-50 border border-white/10 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="border-b border-white/10 p-6">
              <h2 className="text-xl font-bold text-white">Device management</h2>
              <p className="text-sm text-gray-300 mt-1">Manage devices for {selectedUser.email}</p>
            </div>

            <div className="p-6">
              {userDevices.length === 0 ? (
                <p className="text-gray-300 text-center py-8">No devices registered for this user.</p>
              ) : (
                <div className="space-y-4">
                  {userDevices.map((device) => (
                    <div
                      key={device.id}
                      className="border border-white/10 bg-white/5 rounded-lg p-4 flex items-start justify-between gap-4"
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <div className="text-3xl">
                          {device.type === 'mobile' ? '📱' : device.type === 'tablet' ? '📲' : '💻'}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-white">{device.name || 'Unknown Device'}</h3>
                          <div className="text-sm text-gray-300 mt-1 space-y-1">
                            <div><strong>Browser:</strong> {device.browser || 'Unknown'}</div>
                            <div><strong>OS:</strong> {device.os || 'Unknown'}</div>
                            <div><strong>Last Login:</strong> {device.lastLogin ? new Date(device.lastLogin.seconds * 1000).toLocaleString() : 'Unknown'}</div>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleRemoveDevice(device.id)}
                        className="ml-4 px-3 py-1 text-sm text-rose-200 hover:text-white border border-rose-300/40 rounded bg-rose-500/10"
                      >
                        Remove device
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-white/10 p-6 flex justify-end">
              <button
                onClick={() => {
                  setShowDeviceModal(false);
                  setSelectedUser(null);
                  setUserDevices([]);
                }}
                className="px-4 py-2 bg-white/10 border border-white/15 hover:bg-white/15 text-gray-100 rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
//  Solely designed, developed, and maintained by Gareth Morgan.
//  All intellectual property rights reserved.
//  No liability is accepted for unauthorised use, modification, deployment, or maintenance.
// ----------------------------------------------------------------------------------------------
